/**
 * Analytics beacon — envia pageviews e cliques em CTAs pro backend próprio,
 * sem depender do Google Analytics.
 *
 * LGPD: session_id vive no sessionStorage (não persiste após fechar aba),
 * nenhum cookie é setado, nenhum dado pessoal é enviado. Pode rodar sem consent
 * (é analytics operacional, não publicidade).
 *
 * Uso automático: `useRouteBeacon()` no App.js rastreia mudanças de rota.
 * Uso manual: `trackWhatsApp(cta)` antes de abrir o link WA.
 */
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const SESSION_KEY = "raf.analytics.sid";

const genSessionId = () => {
  // 16 chars aleatórios base36 — suficiente pra dedup + curto pro storage
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
};

const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = genSessionId();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    // Se sessionStorage bloqueado (Safari privado), usa memória volátil
    if (!window.__rafSid) window.__rafSid = genSessionId();
    return window.__rafSid;
  }
};

/**
 * Envia um evento pro backend. Falha silenciosamente — nunca deve quebrar
 * a experiência de navegação do visitante.
 *
 * Prefere navigator.sendBeacon quando disponível (funciona mesmo se o usuário
 * fecha a aba imediatamente após o clique). Fallback pra fetch/axios.
 */
export const sendBeacon = (event, meta = {}) => {
  try {
    const payload = {
      session_id: getSessionId(),
      path: window.location.pathname + window.location.search,
      referrer: document.referrer || "",
      event,
      meta,
    };
    const url = `${API}/api/analytics/beacon`;
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    // Fallback — não bloqueia navegação
    axios.post(url, payload, { timeout: 3000 }).catch(() => {});
  } catch { /* ignore */ }
};

export const trackPageview = () => sendBeacon("pageview");
export const trackWhatsApp = (cta = "unknown", extra = {}) =>
  sendBeacon("wa_click", { cta, ...extra });
export const trackCta = (cta, extra = {}) =>
  sendBeacon("cta_click", { cta, ...extra });
